import type { Vec2 } from '../../../geo/types';
import type { ChurchParams } from './churchParams';
import {
  clearance,
  elementaryRanges,
  outsideDistance,
  planBounds,
  sectionsAt,
  spanOver,
  type PlanBounds,
  type PlanFrame,
} from './planFrame';
import {
  CHURCH_PLAN,
  apseRing,
  buttressRoom,
  fitsBand,
  isHall,
  type ChurchPlan,
  type FlechePlan,
  type TowerPlan,
  type TranseptPlan,
  type VesselPlan,
} from './churchModel';
import { planSides, planChapels } from './churchSides';

export { apseRing, CHURCH_PLAN } from './churchModel';
export type {
  ChapelPlan,
  ChurchPlan,
  FlechePlan,
  Side,
  SidePlan,
  TowerPlan,
  TranseptPlan,
  VesselPlan,
} from './churchModel';

function longestSection(frame: PlanFrame, u: number): [number, number] | null {
  const all = sectionsAt(frame, u);
  return all.length === 0 ? null : all.reduce((a, b) => (b[1] - b[0] > a[1] - a[0] ? b : a));
}

interface Weighted {
  readonly value: number;
  readonly weight: number;
}

function weightedMedian(items: readonly Weighted[]): number {
  const sorted = [...items].sort((a, b) => a.value - b.value);
  const total = sorted.reduce((s, i) => s + i.weight, 0);
  let acc = 0;
  for (const item of sorted) {
    acc += item.weight;
    if (acc >= total / 2) return item.value;
  }
  return sorted[sorted.length - 1]?.value ?? 0;
}

/** Length-weighted medians of the section midpoints and widths over the footprint. */
export function sectionStats(frame: PlanFrame, u0: number, u1: number) {
  const mids: Weighted[] = [];
  const widths: Weighted[] = [];
  for (const [a, b] of elementaryRanges(frame, u0, u1)) {
    const span = longestSection(frame, (a + b) / 2);
    if (!span) continue;
    mids.push({ value: (span[0] + span[1]) / 2, weight: b - a });
    widths.push({ value: span[1] - span[0], weight: b - a });
  }
  return { axis: weightedMedian(mids), width: weightedMedian(widths) };
}

function planTower(
  frame: PlanFrame,
  params: ChurchParams,
  bounds: PlanBounds,
  stats: { axis: number; width: number }
): TowerPlan | null {
  if (params.towerHeight <= 0) return null;
  const c = CHURCH_PLAN;
  const tw = params.towerWidth ?? Math.min(c.maxTowerWidth, c.towerShare * stats.width);
  const limit = bounds.u0 + c.towerSearchShare * (bounds.u1 - bounds.u0);
  for (let u = bounds.u0; u <= limit; u += c.step) {
    const span = longestSection(frame, u + tw / 2);
    if (!span) continue;
    const vc =
      Math.abs(span[0] + span[1] - 2 * stats.axis) < tw ? (span[0] + span[1]) / 2 : stats.axis;
    if (!fitsBand(frame, u, u + tw, vc - tw / 2, vc + tw / 2)) continue;
    const ratio = params.spire === 'pyramid' ? c.pyramidRatio : c.needleRatio;
    const spireHeight =
      params.spire === 'none' ? 0 : (params.spireHeight ?? ratio * params.towerHeight);
    return {
      u0: u,
      u1: u + tw,
      vc,
      half: tw / 2,
      height: params.towerHeight,
      spire: params.spire,
      spireHeight,
    };
  }
  return null;
}

/** Spacing of the checks along each apse facet, finer than the dents of the traced outlines. */
const APSE_SAMPLE_STEP = 0.25;

function facetFits(frame: PlanFrame, a: Vec2, b: Vec2): boolean {
  const steps = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / APSE_SAMPLE_STEP));
  for (let k = 1; k < steps; k++) {
    const t = k / steps;
    if (
      outsideDistance(frame, [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]) >
      CHURCH_PLAN.margin
    )
      return false;
  }
  return true;
}

/**
 * The apse corners carry buttresses, so they keep buttress room; the chord ends and the
 * facets between the corners (traced outlines can dent inward there) need only fit.
 */
function apseFits(frame: PlanFrame, vessel: VesselPlan): boolean {
  const ring = apseRing(vessel);
  const last = ring.length - 1;
  const corners = ring.every((p: Vec2, i) =>
    i === 0 || i === last
      ? outsideDistance(frame, p) <= CHURCH_PLAN.margin
      : clearance(frame, p) >= buttressRoom()
  );
  return corners && ring.slice(1).every((b, i) => facetFits(frame, ring[i]!, b));
}

function findEast(frame: PlanFrame, draft: VesselPlan, bounds: PlanBounds): number | null {
  const { step } = CHURCH_PLAN;
  const lo = draft.vc - draft.half;
  const hi = draft.vc + draft.half;
  const start = draft.apse === 'polygonal' ? bounds.u1 - draft.half : bounds.u1;
  for (let c = start; c > draft.u0 + step; c -= step) {
    const vessel = { ...draft, chordU: c };
    if (fitsBand(frame, c - step, c, lo, hi) && apseFits(frame, vessel)) return c;
  }
  return null;
}

function findWest(
  frame: PlanFrame,
  lo: number,
  hi: number,
  from: number,
  to: number
): number | null {
  for (let u = from; u < to - 1; u += CHURCH_PLAN.step) {
    if (fitsBand(frame, u, u + 1, lo, hi)) return u;
  }
  return null;
}

export function draftVessel(
  params: Pick<ChurchParams, 'naveHeight' | 'apse'>,
  vc: number,
  half: number,
  u0: number
): VesselPlan {
  const eaveY = params.naveHeight;
  const ridgeY = eaveY + half * Math.tan(CHURCH_PLAN.roofPitch);
  return { u0, chordU: u0, vc, half, eaveY, ridgeY, apse: params.apse };
}

/** Where the traced nave only widens some way east of the tower, a narrower bay links them. */
function planWestBay(
  frame: PlanFrame,
  params: ChurchParams,
  tower: TowerPlan,
  vessel: VesselPlan
): VesselPlan | null {
  const c = CHURCH_PLAN;
  const u0 = tower.u1 - c.towerOverlap;
  const u1 = vessel.u0 + c.towerOverlap;
  if (u1 - u0 <= 2 * c.towerOverlap + c.step) return null;
  const span = spanOver(frame, u0, u1, tower.vc);
  if (!span) return null;
  const half = Math.min(vessel.half, (span[1] - span[0]) / 2);
  if (half < c.minSide) return null;
  const vc = Math.min(Math.max(vessel.vc, span[0] + half), span[1] - half);
  return { ...draftVessel(params, vc, half, u0), chordU: u1, apse: 'flat' };
}

function planVessel(
  frame: PlanFrame,
  params: ChurchParams,
  tower: TowerPlan | null,
  bounds: PlanBounds,
  stats: { axis: number; width: number }
): { vessel: VesselPlan; westBay: VesselPlan | null } {
  let half = startHalf(params, stats.width);
  const from = tower ? tower.u1 - CHURCH_PLAN.towerOverlap : bounds.u0;
  for (let attempt = 0; attempt < CHURCH_PLAN.shrinkSteps; attempt++) {
    const west = findWest(frame, stats.axis - half, stats.axis + half, from, bounds.u1);
    const draft = west === null ? null : draftVessel(params, stats.axis, half, west);
    const east = draft ? findEast(frame, draft, bounds) : null;
    if (draft && east !== null) {
      const vessel = { ...draft, chordU: east };
      return { vessel, westBay: tower ? planWestBay(frame, params, tower, vessel) : null };
    }
    half *= CHURCH_PLAN.shrinkFactor;
  }
  return { vessel: draftVessel(params, stats.axis, half, from), westBay: null };
}

function startHalf(params: ChurchParams, width: number): number {
  const c = CHURCH_PLAN;
  if (params.naveWidth !== null) return params.naveWidth / (isHall(params) ? 3 : 1) / 2;
  const share = params.aisles >= 2 ? c.aisledNaveShare : 1;
  return Math.min(c.maxNaveWidth, share * width - 2 * c.margin) / 2;
}

function longestMarkedRun(
  ranges: readonly { a: number; b: number; ok: boolean }[]
): [number, number] | null {
  let best: [number, number] | null = null;
  let start: number | null = null;
  ranges.forEach((r, i) => {
    if (r.ok && start === null) start = r.a;
    const closes = r.ok && (i === ranges.length - 1 || !ranges[i + 1]!.ok);
    if (closes && start !== null) {
      if (!best || r.b - start > best[1] - best[0]) best = [start, r.b];
      start = null;
    }
  });
  return best;
}

function planTransept(
  frame: PlanFrame,
  params: ChurchParams,
  vessel: VesselPlan
): TranseptPlan | null {
  if (!params.transept) return null;
  const c = CHURCH_PLAN;
  const width = sectionStats(frame, vessel.u0, vessel.chordU).width;
  const ranges = elementaryRanges(frame, vessel.u0 + vessel.half, vessel.chordU - vessel.half).map(
    ([a, b]) => {
      const span = spanOver(frame, a, b, vessel.vc);
      return { a, b, ok: span !== null && span[1] - span[0] >= width + c.transeptExcess };
    }
  );
  const run = longestMarkedRun(ranges);
  if (!run || run[1] - run[0] < c.transeptMinRatio * 2 * vessel.half) return null;
  const mid = (run[0] + run[1]) / 2;
  const half = Math.min((run[1] - run[0]) / 2, c.transeptMaxRatio * vessel.half);
  const span = spanOver(frame, mid - half, mid + half, vessel.vc);
  const reach = vessel.half + c.armMinReach;
  if (!span) return null;
  const v0 = span[0] + buttressRoom();
  const v1 = span[1] - buttressRoom();
  if (vessel.vc - v0 < reach || v1 - vessel.vc < reach) return null;
  return { u0: mid - half, u1: mid + half, v0, v1, eaveY: vessel.eaveY, ridgeY: vessel.ridgeY };
}

export function planFleche(
  params: Pick<ChurchParams, 'ridgeTurret' | 'spire'>,
  vessel: VesselPlan,
  transept: TranseptPlan | null,
  tower: TowerPlan | null
): FlechePlan | null {
  if (!params.ridgeTurret) return null;
  const u = transept ? (transept.u0 + transept.u1) / 2 : (vessel.u0 + vessel.chordU) / 2;
  const kind = !tower && params.spire === 'pyramid' ? 'pyramid' : 'needle';
  return { u, v: vessel.vc, footY: vessel.ridgeY - CHURCH_PLAN.flecheSink, kind };
}

export function planChurch(frame: PlanFrame, params: ChurchParams): ChurchPlan {
  const bounds = planBounds(frame);
  const stats = sectionStats(frame, bounds.u0, bounds.u1);
  const tower = planTower(frame, params, bounds, stats);
  const { vessel, westBay } = planVessel(frame, params, tower, bounds, stats);
  const transept = planTransept(frame, params, vessel);
  const sides = planSides(frame, params, vessel, transept);
  const chapels = planChapels(frame, params, vessel, sides);
  const fleche = planFleche(params, vessel, transept, tower);
  return { tower, westBay, vessel, sides, chapels, transept, fleche };
}
